import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import {
  useQuery,
} from '@tanstack/react-query';
import {
  ChargeDetailsResponseDTO,
  ClaimDetailsResponseDTO,
} from 'dtos';

type RowDetailsContextValue<TDetails> = {
  requestDetails: (ids: number[]) => void;
  invalidateDetails: (ids: number[]) => void;
  detailsById: Record<number, TDetails>;
  fetchingIds: number[];
};

const RowDetailsContext = createContext<RowDetailsContextValue<unknown>>({
  requestDetails: () => {},
  invalidateDetails: () => {},
  detailsById: {},
  fetchingIds: [],
});

export function useRowDetails<TDetails>(): RowDetailsContextValue<TDetails> {
  return useContext(RowDetailsContext) as RowDetailsContextValue<TDetails>;
}

export function useChargeDetails(): RowDetailsContextValue<ChargeDetailsResponseDTO> {
  return useRowDetails<ChargeDetailsResponseDTO>();
}

export function useClaimDetails(): RowDetailsContextValue<ClaimDetailsResponseDTO> {
  return useRowDetails<ClaimDetailsResponseDTO>();
}

type RowDetailsProviderProps<TDetails> = PropsWithChildren<{
  queryKey: string;
  fetchDetails: (ids: number[]) => Promise<TDetails[]>;
  getDetailsId: (details: TDetails) => number;
}>;

export default function RowDetailsProvider<TDetails>({
  children,
  queryKey,
  fetchDetails,
  getDetailsId,
}: RowDetailsProviderProps<TDetails>) {
  const [requestedIds, setRequestedIds] = useState<number[]>([]);
  const [detailsById, setDetailsById] = useState<Record<number, TDetails>>({});
  const detailsByIdRef = useRef(detailsById);
  const getDetailsIdRef = useRef(getDetailsId);
  const fetchDetailsRef = useRef(fetchDetails);

  getDetailsIdRef.current = getDetailsId;
  fetchDetailsRef.current = fetchDetails;
  detailsByIdRef.current = detailsById;

  const fetchingIds = useMemo(
    () => requestedIds.filter((id) => isNil(detailsById[id])),
    [requestedIds, detailsById],
  );

  const { data } = useQuery<TDetails[], Error>({
    queryKey: [queryKey, fetchingIds],
    queryFn: () => fetchDetailsRef.current(fetchingIds),
    enabled: fetchingIds.length > 0,
  });

  useEffect(() => {
    if (isNil(data) || data.length === 0) {
      return;
    }

    setDetailsById((previous) => {
      const next = { ...previous };
      let didChange = false;

      data.forEach((details) => {
        const id = getDetailsIdRef.current(details);
        if (id > 0 && isNil(previous[id])) {
          next[id] = details;
          didChange = true;
        }
      });

      if (!didChange) {
        return previous;
      }

      detailsByIdRef.current = next;
      return next;
    });
  }, [data]);

  const requestDetails = useCallback((ids: number[]) => {
    const idsToRequest = uniq(
      ids.filter((id) => (
        id > 0 && isNil(detailsByIdRef.current[id])
      )),
    );

    if (idsToRequest.length === 0) {
      return;
    }

    setRequestedIds((previous) => uniq([...previous, ...idsToRequest]));
  }, []);

  const invalidateDetails = useCallback((ids: number[]) => {
    const idsToInvalidate = uniq(ids.filter((id) => id > 0));

    setDetailsById((previous) => {
      const next = { ...previous };
      idsToInvalidate.forEach((id) => {
        delete next[id];
      });
      return next;
    });
    setRequestedIds((previous) => previous.filter((id) => !idsToInvalidate.includes(id)));
  }, []);

  const value = useMemo(() => ({
    requestDetails,
    invalidateDetails,
    detailsById,
    fetchingIds,
  }), [requestDetails, invalidateDetails, detailsById, fetchingIds]);

  return (
    <RowDetailsContext.Provider value={value}>
      {children}
    </RowDetailsContext.Provider>
  );
}
